import { FeatureFlagIds } from "../../utils/featureFlagIds";
import { sort, isNil } from "ramda";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./_organizations-page.module.scss";
import { Organization, Organizations, formatPhoneNumber } from "@amsanalytics/react-shared-library";
import { AppState } from '../../store/rootReducer';
import { OrganizationIds } from "../../utils/constants";
import { useDrawer } from "../../providers/DrawerProvider";
import { IState } from "../../Interfaces/Common/IState";
import { createOrganization, updateOrganization, loadManagementOrganizations } from "../../store/application/types";
import { useConfiguration } from "../../Data/ConfigurationData";
import KendoConfirmationDialog from "../Dialogs/KendoConfirmationDialog";

export interface OrganizationsProps { }

const storageLotChangeConfirmationMessage = "Changing this organization from a Store to a Storage Lot affects lead assignments and removes it from the CRM and LOS except on the Organization Editor page. Do you want to continue?";

export default function OrganizationsPage(props: OrganizationsProps) {
  const [organizationPendingStorageLotChange, setOrganizationPendingStorageLotChange] = React.useState<Organization>();
  // Org-management list — INCLUDES StorageLot orgs (this page is the only place they surface in LOS).
  const organizations: Organization[] = useSelector((state: AppState) => state.applications.managementOrganizations);
  // All states
  const states: IState[] = useSelector((state: AppState) => state.applications.states);
  // Can create indirect applications
  const canCreateIndirectApplications: boolean = useSelector((state: AppState) => state.applications.lookups.canCreateIndirectApplication);
  const { data: configuration } = useConfiguration();
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  // Open and close side drawer
  const [openDrawer, closeDrawer] = useDrawer();
  const dispatch = useDispatch();
  // Load the management org list (includes StorageLot) on mount. The saga also reloads it after create/update.
  React.useEffect(() => {
    dispatch(loadManagementOrganizations());
  }, [dispatch]);
  // Returns the string label for the given organization type id.
  const getOrganizationTypeLabelForId = (typeId: string): string => {
    switch (typeId) {
      case OrganizationIds.bdc:
        return "BDC";
      case OrganizationIds.region:
        return "Region";
      case OrganizationIds.store:
        return "Store";
      case OrganizationIds.storageLot:
        return "Storage Lot";
      default:
        return "N/A";
    }
  };

  // User has added a new organization
  const createNewOrganization = (organization: Organization) => {
    dispatch(createOrganization(organization));
  };

  // User has updated an organization
  const updateOrg = (organization: Organization) => {
    const existingOrganization = organizations.find(
      (org) => org.id?.toLowerCase() === organization.id?.toLowerCase()
    );
    const isChangingStoreToStorageLot =
      existingOrganization?.typeId?.toLowerCase() === OrganizationIds.store &&
      organization.typeId?.toLowerCase() === OrganizationIds.storageLot;

    if (isChangingStoreToStorageLot) {
      setOrganizationPendingStorageLotChange(organization);
      return;
    }

    dispatch(updateOrganization(organization));
  };

  const confirmStorageLotChange = () => {
    if (organizationPendingStorageLotChange) {
      dispatch(updateOrganization(organizationPendingStorageLotChange));
    }

    setOrganizationPendingStorageLotChange(undefined);
  };

  return (
    <div className={styles.organizationsContainer}>
      {organizationPendingStorageLotChange && (
        <KendoConfirmationDialog
          title="Confirm Organization Type Change"
          message={storageLotChangeConfirmationMessage}
          messageStyle={{ textAlign: 'left' }}
          confirmText="Continue"
          cancelText="Cancel"
          onCancel={() => setOrganizationPendingStorageLotChange(undefined)}
          onConfirm={confirmStorageLotChange}
        />
      )}
      <Organizations
        lookups={{
          organizations: sort((first: Organization, second: Organization): number => {
            return !isNil(first) && !isNil(second)
              ? first.name! < second.name!
                ? -1
                : 1
              : 0;
          }, organizations).map((org: Organization) => {
            return {
              ...org,
              typeLabel: getOrganizationTypeLabelForId(org.typeId),
              parentOrganizationName: organizations.find((ao) => ao.id === org.parentOrganizationId)?.name,
              textFromNumber: org.textFromNumber ? formatPhoneNumber(org.textFromNumber) : "",
            } as Organization;
          }),
          states: states,
          organizationTypes: [
            {
              id: OrganizationIds.bdc,
              name: "BDC"
            },
            {
              id: OrganizationIds.region,
              name: "Region"
            },
            {
              id: OrganizationIds.store,
              name: "Store"
            },
            {
              id: OrganizationIds.storageLot,
              name: "Storage Lot"
            }
          ],
          financeLoanClasses: lookups.financeLoanClasses
        }}
        actions={{
          onCreateOrganization: createNewOrganization,
          onUpdateOrganization: updateOrg,
          openDrawer,
          closeDrawer
        }}
        gridProps={{
          disableEditing: false,
          disableFiltering: false,
          disableGrouping: true,
          disablePaging: false,
          disableSorting: false,
        }}
        flags={{
          canCreateIndirectApplications,
          allowDuplicateContacts: configuration?.featureFlags?.[FeatureFlagIds.AllowDuplicateContacts] === true,
          usesDealerReserve: configuration?.featureFlags?.[FeatureFlagIds.UseDealerReserve] === true,
        }}
      />
    </div>
  );
}
